import React from 'react'
import { FileText } from 'lucide-react'

const TermsOfServicePage: React.FC = () => {
  const lastUpdated = "December 2025"

  return (
    <div className="font-outfit text-ink-green-ink dark:text-ink-green-ink bg-ink-green-canvas dark:bg-ink-green-canvas min-h-screen">
      {/* Main content */}
      <div className="relative z-10">
        {/* Hero Section */}
        <section className="relative flex items-center justify-center px-4 sm:px-6 lg:px-8 pt-28 pb-6">
          <div className="container mx-auto max-w-4xl">
            <div className="text-center">
              <div className="inline-flex items-center px-4 py-1.5 bg-ink-green-accent/10 text-ink-green-accent rounded-full text-xs sm:text-sm font-medium mb-4 sm:mb-6 border border-ink-green-accent/20">
                <FileText className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-2" />
                Legal Terms & Conditions
              </div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-medium tracking-[-.03em] text-ink-green-ink dark:text-ink-green-ink mb-4 sm:mb-6 leading-tight">
                Terms of{' '}
                <span className="text-ink-green-accent">Service</span>
              </h1>
              <p className="text-sm sm:text-base lg:text-lg text-ink-green-ink-soft dark:text-ink-green-ink-soft leading-[1.55] max-w-2xl mx-auto">
                Please read these terms carefully before using our services. They govern your relationship with Novahass Partners.
              </p>
              <p className="text-xs sm:text-sm text-ink-green-ink-soft dark:text-ink-green-ink-soft mt-3 sm:mt-4">
                Last updated: {lastUpdated}
              </p>
            </div>
          </div>
        </section>

        {/* Terms Content */}
        <section className="relative pt-4 pb-16">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
            <div className="bg-ink-green-surface dark:bg-ink-green-surface rounded-panel border border-ink-green-edge p-8 md:p-12">
              <div className="text-sm sm:text-base text-ink-green-ink-soft dark:text-ink-green-ink-soft leading-[1.55] space-y-6 sm:space-y-8">

                <div>
                  <h2 className="text-lg sm:text-xl md:text-2xl font-medium tracking-[-.03em] text-ink-green-heading-soft mb-3 sm:mb-4">1. Acceptance of Terms</h2>
                  <p>By accessing and using the Novahass Partners website and services, you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.</p>
                </div>

                <div>
                  <h2 className="text-lg sm:text-xl md:text-2xl font-medium tracking-[-.03em] text-ink-green-heading-soft mb-3 sm:mb-4">2. Description of Service</h2>
                  <p className="mb-3 sm:mb-4">Novahass Partners provides:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Property sourcing and acquisition services</li>
                    <li>Investment opportunity identification and analysis</li>
                    <li>Property development consultation and management</li>
                    <li>Market research reports and intelligence</li>
                    <li>Digital platform access for property listings and opportunities</li>
                  </ul>
                </div>

                <div>
                  <h2 className="text-lg sm:text-xl md:text-2xl font-medium tracking-[-.03em] text-ink-green-heading-soft mb-3 sm:mb-4">3. User Responsibilities</h2>
                  <p className="mb-3 sm:mb-4">As a user of our services, you agree to:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Provide accurate and complete information when requested</li>
                    <li>Maintain the confidentiality of any login credentials</li>
                    <li>Use our services only for lawful purposes</li>
                    <li>Not interfere with or disrupt the services or servers</li>
                    <li>Comply with all applicable local, national, and international laws</li>
                  </ul>
                </div>

                <div>
                  <h2 className="text-lg sm:text-xl md:text-2xl font-medium tracking-[-.03em] text-ink-green-heading-soft mb-3 sm:mb-4">4. Investment Disclaimers</h2>
                  <div className="bg-ink-green-canvas dark:bg-ink-green-canvas rounded-panel p-4 sm:p-6 border border-ink-green-edge mb-3 sm:mb-4">
                    <p className="font-medium text-ink-green-ink dark:text-ink-green-ink mb-2 sm:mb-3">Important Investment Notice</p>
                    <p>All investments involve risk and potential loss of capital. Past performance does not guarantee future results. Property values can go down as well as up, and you may not get back the full amount invested.</p>
                  </div>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>We provide information and opportunities, not financial advice</li>
                    <li>All investment decisions are made at your own discretion and risk</li>
                    <li>We recommend seeking independent financial advice before investing</li>
                    <li>Market conditions and regulations may affect investment performance</li>
                  </ul>
                </div>

                <div>
                  <h2 className="text-lg sm:text-xl md:text-2xl font-medium tracking-[-.03em] text-ink-green-heading-soft mb-3 sm:mb-4">5. Intellectual Property</h2>
                  <p className="mb-3 sm:mb-4">All content on this website, including but not limited to:</p>
                  <ul className="list-disc pl-6 space-y-2 mb-4">
                    <li>Text, graphics, logos, and images</li>
                    <li>Software and source code</li>
                    <li>Research reports and analysis</li>
                    <li>Proprietary data and algorithms</li>
                  </ul>
                  <p>Are the property of Novahass Partners or its licensors and are protected by copyright and other intellectual property laws.</p>
                </div>

                <div>
                  <h2 className="text-lg sm:text-xl md:text-2xl font-medium tracking-[-.03em] text-ink-green-heading-soft mb-3 sm:mb-4">6. Privacy and Data Protection</h2>
                  <p>We are committed to protecting your privacy and handling your personal data in accordance with UK GDPR and data protection laws. Please refer to our Privacy Policy for detailed information about data collection, use, and protection.</p>
                </div>

                <div>
                  <h2 className="text-lg sm:text-xl md:text-2xl font-medium tracking-[-.03em] text-ink-green-heading-soft mb-3 sm:mb-4">7. Limitation of Liability</h2>
                  <p className="mb-3 sm:mb-4">To the maximum extent permitted by law:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>We exclude all liability for any direct, indirect, or consequential losses</li>
                    <li>We do not warrant that our services will be uninterrupted or error-free</li>
                    <li>Our total liability shall not exceed the amount paid for our services</li>
                    <li>This limitation applies to all claims, whether in contract, tort, or otherwise</li>
                  </ul>
                </div>

                <div>
                  <h2 className="text-lg sm:text-xl md:text-2xl font-medium tracking-[-.03em] text-ink-green-heading-soft mb-3 sm:mb-4">8. Service Availability</h2>
                  <p>We reserve the right to:</p>
                  <ul className="list-disc pl-6 mt-4 space-y-2">
                    <li>Modify or discontinue services with reasonable notice</li>
                    <li>Refuse service to any user for any reason</li>
                    <li>Temporarily suspend services for maintenance or updates</li>
                    <li>Update these terms as necessary to reflect service changes</li>
                  </ul>
                </div>

                <div>
                  <h2 className="text-lg sm:text-xl md:text-2xl font-medium tracking-[-.03em] text-ink-green-heading-soft mb-3 sm:mb-4">9. Governing Law</h2>
                  <p>These terms shall be governed by and construed in accordance with the laws of England and Wales. Any disputes arising under these terms shall be subject to the exclusive jurisdiction of the courts of England and Wales.</p>
                </div>

                <div>
                  <h2 className="text-lg sm:text-xl md:text-2xl font-medium tracking-[-.03em] text-ink-green-heading-soft mb-3 sm:mb-4">10. Professional Regulations</h2>
                  <p>Novahass Partners operates in accordance with relevant UK property and financial services regulations. We maintain appropriate professional indemnity insurance and adhere to industry best practices and ethical standards.</p>
                </div>

                <div>
                  <h2 className="text-lg sm:text-xl md:text-2xl font-medium tracking-[-.03em] text-ink-green-heading-soft mb-3 sm:mb-4">11. Contact Information</h2>
                  <p className="mb-3 sm:mb-4">For questions about these Terms of Service:</p>
                  <div className="bg-ink-green-canvas dark:bg-ink-green-canvas rounded-panel p-4 sm:p-6 border border-ink-green-edge">
                    <p><strong>Email:</strong> legal@novahass.partners</p>
                    <p><strong>Address:</strong> 7 Bell Yard, London, WC2A 2JR</p>
                    <p><strong>Phone:</strong> Available upon request</p>
                    <p><strong>Business Hours:</strong> Monday - Friday, 9:00 AM - 6:00 PM GMT</p>
                  </div>
                </div>

                <div>
                  <h2 className="text-lg sm:text-xl md:text-2xl font-medium tracking-[-.03em] text-ink-green-heading-soft mb-3 sm:mb-4">12. Changes to Terms</h2>
                  <p>We reserve the right to modify these terms at any time. Users will be notified of significant changes via email or website notification. Continued use of our services after changes constitutes acceptance of the new terms.</p>
                </div>

              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  )
}

export default TermsOfServicePage
